/**
 * Multiplayer sync PROTOCOL (Phase A — docs/MODELER.md "Multiplayer"): the pure,
 * transport-agnostic core. A transport (Supabase Realtime today; y-websocket/
 * PartyKit/anything tomorrow) provides `post(event, payload)` and feeds inbound
 * messages to `handle...`; this module owns the Yjs mechanics:
 *
 *  - live edits: every local doc update (origin !== REMOTE_ORIGIN) posts out;
 *    inbound updates apply with REMOTE_ORIGIN so per-user undo (undo.ts
 *    trackedOrigins) and the app's local-dirty tracking skip them by construction.
 *  - join catch-up (symmetric): a joiner posts `sv` (its state vector, reply:false);
 *    a receiver answers with the missing diff AND its own `sv` (reply:true) exactly
 *    once — reply:true never triggers another sv, so no loops.
 *  - payloads are base64 chunked at CHUNK_CHARS (Realtime caps message sizes);
 *    the reassembler is per-connection state.
 *
 * The doc + its Postgres snapshots remain the source of truth — swapping the pipe
 * never touches the kernel, commands, or UI (the portability promise).
 */
import * as Y from 'yjs'

export const REMOTE_ORIGIN = 'bim-sync-remote'

const CHUNK_CHARS = 180_000          // base64 chars per message (~135KB binary)

// distinct, readable-on-white peer colors — assigned by stable hash of the user id
const PEER_COLORS = ['#2f7c8c', '#b3572a', '#5b7c2f', '#7c2f6e', '#2a5cb3', '#8c6b1f']
export function peerColor(userId: string): string {
  let h = 0
  const s = String(userId)
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return PEER_COLORS[h % PEER_COLORS.length]
}

export interface SyncChunk { id: string; i: number; n: number; data: string; reply?: boolean; from?: string }

// pure base64 (no btoa/atob — the kernel has no browser/DOM globals by decree)
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
const B64_REV: Record<string, number> = {}
for (let i = 0; i < B64.length; i++) B64_REV[B64[i]] = i

export function b64encode(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i], b = i + 1 < bytes.length ? bytes[i + 1] : 0, c = i + 2 < bytes.length ? bytes[i + 2] : 0
    out += B64[a >> 2] + B64[((a & 3) << 4) | (b >> 4)]
    out += i + 1 < bytes.length ? B64[((b & 15) << 2) | (c >> 6)] : '='
    out += i + 2 < bytes.length ? B64[c & 63] : '='
  }
  return out
}
export function b64decode(s: string): Uint8Array {
  const clean = s.replace(/=+$/, '')
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4))
  let o = 0
  for (let i = 0; i < clean.length; i += 4) {
    const a = B64_REV[clean[i]], b = B64_REV[clean[i + 1]]
    const c = i + 2 < clean.length ? B64_REV[clean[i + 2]] : 0
    const d = i + 3 < clean.length ? B64_REV[clean[i + 3]] : 0
    out[o++] = (a << 2) | (b >> 4)
    if (i + 2 < clean.length) out[o++] = ((b & 15) << 4) | (c >> 2)
    if (i + 3 < clean.length) out[o++] = ((c & 3) << 6) | d
  }
  return out
}

/** split a base64 payload into self-describing chunks (id groups a message) */
export function chunkPayload(b64: string, id: string): SyncChunk[] {
  const n = Math.max(1, Math.ceil(b64.length / CHUNK_CHARS))
  const out: SyncChunk[] = []
  for (let i = 0; i < n; i++) out.push({ id, i, n, data: b64.slice(i * CHUNK_CHARS, (i + 1) * CHUNK_CHARS) })
  return out
}

/** reassembler: feed chunks, get the whole base64 once complete (else null) */
export function makeReassembler(): (chunk: SyncChunk) => string | null {
  const partial = new Map<string, { parts: (string | null)[]; got: number }>()
  return (chunk) => {
    if (chunk.n === 1) return chunk.data
    let entry = partial.get(chunk.id)
    if (!entry) { entry = { parts: new Array<string | null>(chunk.n).fill(null), got: 0 }; partial.set(chunk.id, entry) }
    if (entry.parts[chunk.i] === null) { entry.parts[chunk.i] = chunk.data; entry.got++ }
    if (entry.got < chunk.n) return null
    partial.delete(chunk.id)
    return (entry.parts as string[]).join('')
  }
}

export interface SyncProtocolHandle {
  /** call when the transport connects (starts the join handshake) */
  start(): void
  /** inbound 'yjs-update' message */
  handleUpdate(chunk: SyncChunk): void
  /** inbound 'sv' message */
  handleSv(chunk: SyncChunk): void
  /**
   * Periodic self-heal (r20b): re-post my state vector with reply:true — peers
   * send back anything I'm missing (empty diff = no traffic when in sync) and
   * never re-request in return, so there is no sv storm. Individual dropped
   * broadcasts (rate limits, socket blips) become "converged by the next tick"
   * instead of silent divergence.
   */
  resync(): void
  destroy(): void
}

/**
 * Wire a Y.Doc to a transport. `post` sends one JSON-able payload under an event
 * name; the transport must deliver peers' posts (not the sender's own echo).
 */
export function createSyncProtocol(
  ydoc: Y.Doc,
  peerId: string,
  post: (event: 'yjs-update' | 'sv', payload: SyncChunk) => void,
): SyncProtocolHandle {
  const reassemble = makeReassembler()
  let msgSeq = 0
  let started = false

  const sendDoc = (event: 'yjs-update' | 'sv', bytes: Uint8Array, extra: Partial<SyncChunk> = {}) => {
    for (const chunk of chunkPayload(b64encode(bytes), `${peerId}:${msgSeq++}`)) {
      post(event, { ...chunk, ...extra, from: peerId })
    }
  }

  const updateHandler = (update: Uint8Array, origin: unknown) => {
    if (origin === REMOTE_ORIGIN || !started) return
    sendDoc('yjs-update', update)
  }
  ydoc.on('update', updateHandler)

  return {
    start() {
      started = true
      sendDoc('sv', Y.encodeStateVector(ydoc))
    },
    handleUpdate(chunk) {
      const whole = reassemble(chunk)
      if (whole === null) return
      Y.applyUpdate(ydoc, b64decode(whole), REMOTE_ORIGIN)
    },
    handleSv(chunk) {
      const whole = reassemble(chunk)
      if (whole === null) return
      const diff = Y.encodeStateAsUpdate(ydoc, b64decode(whole))
      if (diff.length > 2) sendDoc('yjs-update', diff)     // 2 bytes = the empty update
      if (!chunk.reply) sendDoc('sv', Y.encodeStateVector(ydoc), { reply: true })
    },
    resync() {
      if (started) sendDoc('sv', Y.encodeStateVector(ydoc), { reply: true })
    },
    destroy() {
      started = false
      ydoc.off('update', updateHandler)
    },
  }
}
